import { Clipboard } from "@capacitor/clipboard";
import classNames from "classnames";
import ons from "onsenui";
import React from "react";
import { STRINGS } from "../strings";
import { translate } from "../translate";
import { isClipboardAvailable } from "../utils/clipboard";
import { withDefault } from "../utils/withDefault";
import { CardButton } from "./CardButton/CardButton";
import { AppCard, AppCardContent } from "./Generic/AppCard";
import { If } from "./Generic/If";
import QuestionHeader from "./QuestionHeader";
import { RequiredDocAnswerableQuestionProps } from "./RequiredDocAnswerableQuestion";
import RequiredDocReadonlyAnswer from "./RequiredDocReadonlyAnswer";
import { scrollToViewAfterKeyboardOpen } from "./ScheduledVisitDocScreenUtils";
import { AppIcon } from "./Generic/AppIcon";
import { iconColors } from "./Generic/AppIconColors";
import { OutlineMicIcon } from "../utils/icones";
import { SpeechRecognitionModal } from "./SpeechRecognitionModal";
import useOpen from "../hooks/useOpen";

interface Props extends RequiredDocAnswerableQuestionProps {}

const RequiredDocLongText = (props: Props) => {
  const { actions, isOpen } = useOpen();

  const handleAppCardClick = () => {
    if (props.readonly && props.onClickWhileDisabled !== undefined) {
      props.onClickWhileDisabled();
    }
  };

  function handleClickRecord() {
    if (props.readonly) {
      return;
    }
    actions.open();
  }

  return (
    <AppCard
      className={classNames({ __invalid: !props.isValid })}
      onClick={() => handleAppCardClick()}
    >
      <QuestionHeader
        text={props.questionText}
        isRequired={props.isRequired}
        notes={props.notes}
        isReadonly={props.readonly}
        recordAction={
          <AppIcon
            onClick={handleClickRecord}
            fill={iconColors.get("primary")}
            icon={<OutlineMicIcon width={24} />}
          />
        }
      />
      <AppCardContent onClick={(e) => scrollToViewAfterKeyboardOpen(e)}>
        {props.readonly ? (
          <RequiredDocReadonlyAnswer currentAnswer={props.currentAnswer} />
        ) : (
          <textarea
            placeholder={translate(STRINGS.ENTER_TEXT_HERE)}
            name={props.questionText}
            rows={6}
            disabled={props.readonly}
            className="ScheduledVisitDocScreen__textarea textarea textarea--transparent"
            onChange={(event) => props.handleOnInsertAnswer(props.questionId, event.target.value)}
            value={withDefault(props.currentAnswer, withDefault(props.prefilledAnswer, ""))}
          />
        )}
        <div className="ScheduledVisitDocScreen_longText_CopyPasteIcons">
          {isClipboardAvailable() ? (
            <>
              <CardButton
                variation="outline"
                onClick={(e) => {
                  e.stopPropagation();
                  if (typeof props.currentAnswer === "string") {
                    Clipboard.write({ string: props.currentAnswer })
                      .then(() =>
                        ons.notification.toast("Copied text to clipboard", {
                          timeout: 1000,
                        })
                      )
                      .catch((err) =>
                        ons.notification.toast(`Error copying text to clipboard ${err}`, {
                          timeout: 1000,
                        })
                      );
                  }
                }}
              >
                {translate(STRINGS.COPY)}
              </CardButton>
              <If condition={!props.readonly}>
                <CardButton
                  variation="outline"
                  onClick={(e) => {
                    e.stopPropagation();
                    Clipboard.read()
                      .then(({ value }) => props.handleOnInsertAnswer(props.questionId, value))
                      .catch((err) => {
                        ons.notification.toast(`Error pasting ${err}`, {
                          timeout: 1000,
                        });
                      });
                  }}
                >
                  {translate(STRINGS.PASTE)}
                </CardButton>
              </If>
            </>
          ) : null}
        </div>
      </AppCardContent>
      <If condition={isOpen && !props.readonly}>
        <SpeechRecognitionModal
          isOpen={isOpen}
          onStop={(text) => {
            props.handleOnInsertAnswer(
              props.questionId,
              typeof props.currentAnswer === "string" ? `${props.currentAnswer} ${text}` : text
            );
            actions.dismiss();
          }}
          onCancel={actions.dismiss}
        />
      </If>
    </AppCard>
  );
};

export default React.memo(RequiredDocLongText);
